import { api } from './client';

export interface BudgetAllocation {
  id: string;
  user_id: string;
  brand_id: string;
  optimizer: string;
  channel: string;
  month: string;
  daily_budget: number;
  monthly_budget: number;
  today_spent: number;
  month_spent: number;
  alert_level: number;
  alert_status: string;
  note?: string;
  created_at: string;
  updated_at: string;
}

export interface BudgetListParams {
  brand_id: string;
  site_id?: string;
  page?: number;
  size?: number;
  search?: string;
  month?: string;
}

export interface BudgetListResponse {
  list: BudgetAllocation[];
  total: number;
  page: number;
  size: number;
  total_page: number;
}

export interface BudgetCreateRequest {
  optimizer: string;
  month: string;
  daily_budget: number;
  monthly_budget: number;
  note?: string;
}

export interface BudgetUpdateRequest {
  optimizer?: string;
  month?: string;
  daily_budget?: number;
  monthly_budget?: number;
  note?: string;
}

// AccountOption represents a user who has bound platform accounts.
export interface AccountOption {
  user_id: string;
  user_name: string;
}

// ChannelOption represents a platform channel with budget/spend for transfer modal.
export interface ChannelOption {
  channel: string;
  monthly_budget: number;
  month_spent: number;
  available: number;
}

export interface ChannelAllocation {
  id: string;
  user_id: string;
  brand_id: string;
  optimizer: string;
  channel: string;
  month: string;
  daily_budget: number;
  monthly_budget: number;
  today_spent: number;
  month_spent: number;
  roi: number;
  cpa: number;
}

export interface SaveChannelAllocationsRequest {
  optimizer: string;
  month: string;
  allocations: Array<{
    channel: string;
    daily_budget: number;
    monthly_budget: number;
    roi: number;
    cpa: number;
  }>;
}

export interface BudgetTransfer {
  id: string;
  user_id: string;
  brand_id: string;
  optimizer: string;
  from_channel: string;
  to_channel: string;
  amount: number;
  reason: string;
  status: string;
  created_at: string;
}

export interface CreateTransferRequest {
  optimizer: string;
  from_channel: string;
  to_channel: string;
  amount: number;
  reason: string;
}

export interface AlertRule {
  id: string;
  user_id: string;
  brand_id: string;
  level1_threshold: number;
  level2_threshold: number;
  level3_threshold: number;
  level1_channels: string[];
  level2_channels: string[];
  level3_channels: string[];
  auto_pause: boolean;
  channel_level_enabled: boolean;
  quiet_start: string;
  quiet_end: string;
}

export interface AlertEvent {
  id: string;
  optimizer: string;
  level: number;
  trigger_dim: string;
  spent: number;
  budget: number;
  action: string;
  result: string;
  created_at: string;
}

export interface StrategySuggestion {
  id: string;
  optimizer: string;
  mode: string;
  title: string;
  summary: string;
  recommendation_json: string;
  expected_roi: number;
  expected_profit_delta: number;
  status: string;
  created_at: string;
}

export interface StrategyEffect {
  id: string;
  optimizer: string;
  strategy_mode: string;
  adjustment: string;
  t1_roi: number;
  t7_roi: number;
  evaluation: string;
  created_at: string;
}

export interface ChangeLog {
  id: string;
  operator: string;
  action_type: string;
  target: string;
  detail: string;
  reason: string;
  created_at: string;
}

function bqs(brandID: string, siteID?: string): string {
  const qs = new URLSearchParams({ brand_id: brandID });
  if (siteID) qs.set('site_id', siteID);
  return qs.toString();
}

export const budgetsApi = {
  list(params: BudgetListParams) {
    return api.get<BudgetListResponse>('/budgets', { params });
  },
  get(id: string, brandID: string, siteID?: string) {
    return api.get<BudgetAllocation>(`/budgets/${id}`, { params: { brand_id: brandID, site_id: siteID } });
  },
  create(data: BudgetCreateRequest, brandID: string, siteID?: string) {
    return api.post<BudgetAllocation>(`/budgets?${bqs(brandID, siteID)}`, data);
  },
  update(id: string, data: BudgetUpdateRequest, brandID: string, siteID?: string) {
    return api.patch<BudgetAllocation>(`/budgets/${id}?${bqs(brandID, siteID)}`, data);
  },
  remove(id: string, brandID: string, siteID?: string) {
    return api.delete<void>(`/budgets/${id}?${bqs(brandID, siteID)}`);
  },

  listChannelAllocations(brandID: string, optimizer?: string, month?: string, siteID?: string) {
    return api.get<{ list: ChannelAllocation[] }>('/budgets/channel-allocations', {
      params: { brand_id: brandID, site_id: siteID, optimizer, month },
    });
  },
  saveChannelAllocations(brandID: string, data: SaveChannelAllocationsRequest, siteID?: string) {
    return api.put<{ success: boolean }>(`/budgets/channel-allocations?${bqs(brandID, siteID)}`, data);
  },

  listTransfers(brandID: string, status?: string, month?: string, siteID?: string) {
    return api.get<{ list: BudgetTransfer[] }>('/budgets/transfers', {
      params: { brand_id: brandID, site_id: siteID, status, month },
    });
  },
  createTransfer(brandID: string, data: CreateTransferRequest, siteID?: string) {
    return api.post<BudgetTransfer>(`/budgets/transfers?${bqs(brandID, siteID)}`, data);
  },
  updateTransferStatus(brandID: string, id: string, status: string, siteID?: string) {
    return api.patch<{ success: boolean }>(`/budgets/transfers/${id}/status?${bqs(brandID, siteID)}`, { status });
  },

  getAlertRule(brandID: string, siteID?: string) {
    return api.get<AlertRule>('/budgets/alert-rules', { params: { brand_id: brandID, site_id: siteID } });
  },
  updateAlertRule(brandID: string, data: AlertRule, siteID?: string) {
    return api.put<AlertRule>(`/budgets/alert-rules?${bqs(brandID, siteID)}`, data);
  },
  listAlertEvents(brandID: string, scope: 'current' | 'history', level?: string, month?: string, siteID?: string) {
    return api.get<{ list: AlertEvent[] }>('/budgets/alert-events', {
      params: { brand_id: brandID, site_id: siteID, scope, level, month },
    });
  },

  listStrategies(brandID: string, mode: string, month?: string, siteID?: string) {
    return api.get<{ list: StrategySuggestion[] }>('/budgets/strategies', {
      params: { brand_id: brandID, site_id: siteID, mode, month },
    });
  },
  adoptStrategy(brandID: string, id: string, siteID?: string) {
    return api.post<{ success: boolean }>(`/budgets/strategies/${id}/adopt?${bqs(brandID, siteID)}`);
  },
  listStrategyEffects(brandID: string, siteID?: string) {
    return api.get<{ list: StrategyEffect[] }>('/budgets/strategy-effects', {
      params: { brand_id: brandID, site_id: siteID },
    });
  },

  listChangeLogs(brandID: string, params?: { action_type?: string; page?: number; size?: number; month?: string }, siteID?: string) {
    return api.get<{ list: ChangeLog[]; total: number; page: number; size: number; total_page: number }>('/budgets/change-logs', {
      params: { brand_id: brandID, site_id: siteID, ...params },
    });
  },

  getAccountOptions(brandID: string, siteID?: string) {
    return api.get<{ list: AccountOption[] }>('/budgets/account-options', {
      params: { brand_id: brandID, site_id: siteID },
    });
  },

  getChannelOptions(brandID: string, optimizer: string, siteID?: string) {
    return api.get<{ list: ChannelOption[] }>('/budgets/channel-options', {
      params: { brand_id: brandID, site_id: siteID, optimizer },
    });
  },
};
